import React, { useState, useEffect, useMemo } from 'react';
// @ts-ignore
import { useAppContext } from '../../Services/AppContext';
import apiClient from '../../Services/ApiClient';
import * as yup from 'yup';
import moment, { Moment } from 'moment';
import TimePicker from '../Base/TimePicker/TimePicker';
import DatePicker from '../Base/DatePicker/DatePicker';
import FormControl from '../Form/FormControl';
import SubmitButton from '../Button/SubmitButton';
import MultiSelect from '../Base/MultiSelect/MultiSelect';
import Select, { SelectValue } from '../Base/Select/Select';
import withForm from '../Form/withForm';
import { getMomentDate } from '../../Utils/dates.util';
import useTranslation from '../../Hooks/useTranslation';
import { checkIsTimeSameOrAfter } from '../../Modules/Events/Services/eventInterval.service';
import Checkbox from '../Base/Checkbox/Checkbox';
import FormCondition from '../Form/FormCondition';
import FormattedPriceInputField from '../../Modules/Base/Components/Inputs/FormattedPriceInput/FormattedPriceInputField';
import Text from '../Base/Text/Text';
import { Theme } from '../../Enums/Theme.enum';
import LinkButton from '../Button/LinkButton';
import SubmitButtonWithConfirmation from '../Button/SubmitButtonWithConfirmation';
import {
	CaseTypeType,
	useFullWorkplaceCaseTypesQuery,
} from '../../graphql/graphql';
import Loading from '../Loading';
import { useInputValidation } from '../../Modules/Base/Hooks/useInputValidation';
import FullPageLoading from '../Utils/FullPageLoading/FullPageLoading';
import { getFormattedPriceAsFloat } from '../../Modules/Base/Services/priceFormat.service';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import FormWrapper from '../Form/FormWrapper';
import useApiClient from '../../Modules/Base/Hooks/useApiClient';
import FormContainer from '../Form/FormContainer';
import CheckboxField from '../Form/components/CheckboxField';
import ScheduleInSpecificDaysInWeekFields from '../../Modules/ServiceSchedule/Components/ScheduleInSpecificDaysInWeekFields';
import { RepeatInterval } from '../../Modules/ServiceSchedule/Enums/RepeatInterval.enum';

const days: Record<string, string> = {
	Monday: 'mon',
	Tuesday: 'tue',
	Wednesday: 'wed',
	Thursday: 'thu',
	Friday: 'fri',
	Saturday: 'sat',
	Sunday: 'sun',
};

interface ICalendarWorkplaceOrderingSettingsEvent {
	selectedDate: Moment;
	onSuccess?: () => void;
	workplaceId?: string;
	schedule?: any;
	onDelete: () => void;
}

const CalendarWorkplaceOrderingSettingsEvent = (
	props: ICalendarWorkplaceOrderingSettingsEvent,
) => {
	const { selectedDate, onSuccess, workplaceId, schedule, onDelete } = props;
	const { currency, vatRate } = useAppContext();
	const [showRepeatTo, setShowRepeatTo] = useState(false);
	const { displayError } = useApiClient();
	const { t } = useTranslation();
	const [isPaid, setIsPaid] = useState(false);
	const { getPriceInputValidation } = useInputValidation();
	const [isLoading, setIsLoading] = useState(false);

	const workplaceCaseTypes = useFullWorkplaceCaseTypesQuery({
		workplaceId,
		caseTypeTypes: [CaseTypeType.CaseType],
		take: 999,
	});

	const caseTypes = useMemo(() => {
		const tempCaseTypes: SelectValue[] = [];
		workplaceCaseTypes?.data?.workplaceCaseTypes?.data?.forEach(item => {
			if (
				item?.caseType?.eventInterval &&
				(item?.caseType?.canBookOnline || item?.caseType?.canBookOnSite)
			)
				tempCaseTypes.push({
					label: item?.caseType?.label ?? item?.caseType?.id,
					value: item?.id,
				});
		});
		return tempCaseTypes;
	}, [workplaceCaseTypes]);

	useEffect(() => {
		if (schedule) {
			setIsPaid(
				schedule.event.extendedProps?.serviceScheduleItem?.price
					?.priceWithVatRaw > 0,
			);
		}
	}, []);

	// @ts-ignore
	const handleUpdate = async function (values, updateFuture = false) {
		values.date = getMomentDate(values.date);

		const { date, caseType } = values;
		let { timeFrom, timeTo } = values;
		const tempCaseTypes = [];
		for (const caseTypeItem of caseType || []) {
			tempCaseTypes.push(caseTypeItem.value);
		}

		timeFrom = getMomentDate(timeFrom);
		timeTo = getMomentDate(timeTo);

		const updateData = {
			dateFrom: date.set({
				hour: 0,
				minute: 0,
				second: 0,
			}),
			dateTo: date.clone().set({
				hour: 23,
				minute: 59,
				second: 59,
			}),
			day: days[date.format('dddd')],
			timeFrom: timeFrom.format('HH:mmZ'),
			timeTo: timeTo.format('HH:mmZ'),
			workplaceCaseTypeIds: tempCaseTypes,
			price:
				values?.price && isPaid
					? {
							priceWithVat: getFormattedPriceAsFloat(values.price),
							currency,
							vatRate,
					  }
					: null,
		};

		const { repeatable } = schedule.event.extendedProps;

		if (repeatable) {
			updateData.updateFuture = updateFuture;
		} else {
			if (
				values.repeatInterval &&
				values.repeatInterval.value != RepeatInterval.NEVER
			) {
				updateData.repeatInterval = values.repeatInterval.value;
				updateData.repeatTo = values.repeatTo;
				if (
					values.repeatInterval.value === RepeatInterval.SPECIFIC_DAYS_IN_WEEK
				) {
					updateData.specificDaysInWeek = values.specificDaysInWeek;
				}
			}
		}

		await apiClient.patch(
			'/workplaces/' +
				workplaceId +
				'/services-schedule/' +
				schedule.event.extendedProps.scheduleId +
				'/items/' +
				schedule.event.extendedProps.scheduleItemId,
			updateData,
		);

		onSuccess?.();
	};

	const handleToggleRepeat = () => {
		if (!formMethods) {
			return;
		}
		const formValues = formMethods.getValues();
		setShowRepeatTo(formValues?.repeatInterval?.value != RepeatInterval.NEVER);
	};

	// @ts-ignore
	const handleCreate = async function (values) {
		setIsLoading(true);
		values.date = getMomentDate(values.date);

		const tempCaseTypes = [];
		for (const caseType of values.caseType || []) {
			tempCaseTypes.push(caseType.value);
		}

		const { date } = values;
		let { timeFrom, timeTo } = values;

		const timeFromMoment = moment(timeFrom);
		const timeToMoment = moment(timeTo);
		timeFrom = date.clone().set({
			hour: timeFromMoment.hours(),
			minute: timeFromMoment.minutes(),
			second: timeFromMoment.seconds(),
		});
		timeTo = date.clone().set({
			hour: timeToMoment.hours(),
			minute: timeToMoment.minutes(),
			second: timeToMoment.seconds(),
		});

		const dateTo = date.clone().set({
			hour: 23,
			minute: 59,
			second: 59,
		});
		const dateFrom = date.set({
			hour: 0,
			minute: 0,
			second: 0,
		});

		try {
			const serviceSchedule = await apiClient.post(
				'/workplaces/' + workplaceId + '/services-schedule/',
				{
					dateFrom: dateFrom.format(),
					dateTo: dateTo.format(),
					form: 'standard',
					type: 'online_order',
				},
			);
			if (serviceSchedule) {
				const scheduleId = serviceSchedule.data.id;
				const dataToSend = {
					day: days[date.format('dddd')],
					timeFrom: moment(timeFrom).format('HH:mmZ'),
					timeTo: moment(timeTo).format('HH:mmZ'),
					workplaceCaseTypeIds: tempCaseTypes,
					repeatTo: values.repeatTo,
					repeatInterval:
						values.repeatInterval.value === RepeatInterval.NEVER
							? null
							: values.repeatInterval.value,
					specificDaysInWeek:
						values.repeatInterval.value === RepeatInterval.SPECIFIC_DAYS_IN_WEEK
							? values.specificDaysInWeek
							: undefined,
					price:
						values?.price && isPaid
							? {
									priceWithVat: getFormattedPriceAsFloat(values.price),
									currency,
									vatRate,
							  }
							: undefined,
				};
				const serviceScheduleItems = await apiClient.post(
					'/workplaces/' +
						workplaceId +
						'/services-schedule/' +
						scheduleId +
						'/items/',
					dataToSend,
				);
				onSuccess?.();
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	const defaultCaseTypes = [];

	if (schedule) {
		for (const caseType of schedule.event.extendedProps?.caseTypes || []) {
			defaultCaseTypes.push({
				value: caseType.value,
				label: caseType.label,
			});
		}
	}

	const validationSchema = yup.object({
		date: yup
			.date()
			.required(t('validation.calendarWorkplaceOrderingSettingsEvent.date')),
		timeFrom: yup
			.date()
			.nullable()
			.default(undefined)
			.required(
				t('validation.calendarWorkplaceOrderingSettingsEvent.timeFrom'),
			),
		timeTo: yup
			.date()
			.nullable()
			.default(undefined)
			.test(
				'is-greater',
				t('validation.calendarWorkplaceOrderingSettingsEvent.timeDiff') ?? ' ',
				function () {
					const { date, timeFrom, timeTo } = this.parent;
					return checkIsTimeSameOrAfter(date, timeFrom, timeTo);
				},
			)
			.required(t('validation.calendarWorkplaceOrderingSettingsEvent.timeTo')),
		repeatInterval: yup
			.mixed()
			.required(
				t('validation.calendarWorkplaceOrderingSettingsEvent.repeatInterval'),
			),
		repeatTo: yup.mixed().when('repeatInterval', {
			is: ({ value }) => value !== RepeatInterval.NEVER,
			then: yup
				.date()
				.required(
					t('validation.calendarWorkplaceOrderingSettingsEvent.repeatTo'),
				)
				.default(undefined),
		}),
		caseType: yup
			.mixed()
			.required(
				t('validation.calendarWorkplaceOrderingSettingsEvent.caseType'),
			),
		price: !isPaid
			? yup.mixed().notRequired()
			: getPriceInputValidation(
					'validation.calendarWorkplaceOrderingSettingsEvent.price',
					true,
			  ),
		specificDaysInWeek: yup
			.mixed()
			.when('repeatInterval', {
				is: ({ value }) => value === RepeatInterval.SPECIFIC_DAYS_IN_WEEK,
				then: yup
					.object({
						mon: yup.boolean().required(),
						tue: yup.boolean().required(),
						wed: yup.boolean().required(),
						thu: yup.boolean().required(),
						fri: yup.boolean().required(),
						sat: yup.boolean().required(),
						sun: yup.boolean().required(),
					})
					.test(
						'at-least-one-true',
						t(
							'validation.calendarWorkplaceOrderingSettingsEvent.specificDaysInWeek',
						),
						days => Object.values(days).some(Boolean),
					),
			})
			.default(undefined),
	});

	const formMethods = useForm({
		defaultValues: {
			date: schedule ? moment(schedule.event.start) : selectedDate,
			timeFrom: schedule?.event?.start
				? getMomentDate(schedule.event.start)
				: undefined,
			timeTo: schedule?.event?.end
				? getMomentDate(schedule.event.end)
				: undefined,
			caseType: schedule ? defaultCaseTypes : null,
			repeatInterval: { value: RepeatInterval.NEVER, label: t('base.never') },
			price: schedule
				? parseFloat(
						schedule.event.extendedProps?.serviceScheduleItem?.price
							?.priceWithVatRaw || 0,
				  ) || ''
				: '',
			isPaid: schedule
				? schedule?.event.extendedProps?.serviceScheduleItem?.price
						?.priceWithVatRaw > 0
				: false,
		},
		resolver: yupResolver(validationSchema),
	});

	const isPaidValue = formMethods.watch('isPaid');

	useEffect(() => {
		setIsPaid(isPaidValue);
	}, [isPaidValue]);

	if (workplaceCaseTypes?.isLoading) {
		return <Loading />;
	}

	return (
		<>
			{isLoading && <FullPageLoading />}
			<FormWrapper formMethods={formMethods}>
				<div className='mb-3'>
					<Text
						tx='component.orderingSettings.patientCanOrderByHimselfToTheseTerms'
						style={{ fontWeight: 600 }}
					/>
				</div>
				<FormControl label='base.shared.date' name='date' isRequired>
					<DatePicker />
				</FormControl>
				<div className={'row'}>
					<div className={'col-6'}>
						<FormControl label='base.shared.from' name='timeFrom' isRequired>
							<TimePicker />
						</FormControl>
					</div>
					<div className={'col-6'}>
						<FormControl label='base.shared.to' name='timeTo' isRequired>
							<TimePicker />
						</FormControl>
					</div>
				</div>
				{(!schedule || !schedule?.event?.extendedProps?.repeatable) && (
					<div className={'row'}>
						<div className={'col-6'}>
							<FormControl
								label='base.repeat'
								name='repeatInterval'
								onChange={handleToggleRepeat}>
								<Select
									options={[
										{ value: RepeatInterval.NEVER, label: t('base.never') },
										{
											value: RepeatInterval.EVERY_WORKING_DAY,
											label: t(
												'component.calendarWorkplaceOrderingSettingsEvent.everyWorkingDay',
											),
										},
										{
											value: RepeatInterval.EVERY_WEEK,
											label: t('base.everyWeek'),
										},
										{
											value: RepeatInterval.EVERY_FORTNIGHT,
											label: t('base.everyFortnight'),
										},
										{
											value: RepeatInterval.EVERY_MONTH,
											label: t('base.everyMonth'),
										},
										{
											value: RepeatInterval.SPECIFIC_DAYS_IN_WEEK,
											label: t('base.specificDaysInWeek'),
										},
									]}
								/>
							</FormControl>
						</div>
						{showRepeatTo && (
							<div className={'col-6'}>
								<FormControl
									label='base.repeatTo'
									name='repeatTo'
									hideRequired={true}>
									<DatePicker />
								</FormControl>
							</div>
						)}
					</div>
				)}
				<ScheduleInSpecificDaysInWeekFields text='component.calendarWorkplaceOrderingSettingsEvent.selectDayWhenOrderingShouldRepeat' />
				<FormControl
					label='base.pickCaseType'
					name='caseType'
					labelTooltip='component.calendarWorkplaceOrderingSettingsEvent.tooltip'
					isRequired>
					<MultiSelect
						placeholder={t('base.shared.pick')}
						options={caseTypes}
						isSearchable={true}
					/>
				</FormControl>
				<FormControl name='isPaid'>
					<Checkbox
						tx='component.calendarWorkplaceOrderingSettingsEvent.isPaid'
						value={isPaid}
					/>
				</FormControl>
				<FormCondition controlName='isPaid' equal={true}>
					<div className='row'>
						<div className='col-lg-6'>
							<FormattedPriceInputField
								name='price'
								label='component.calendarWorkplaceOrderingSettingsEvent.price'
								placeholder='placeholders.enter'
								isRequired
							/>
						</div>
					</div>
				</FormCondition>
				<div
					className={'d-flex flex-row align-items-center mt-4'}
					style={{ gap: '10px' }}>
					{!schedule ? (
						<SubmitButton onSubmit={handleCreate} disabled={isLoading} />
					) : (
						<>
							{schedule?.event?.extendedProps?.repeatable ? (
								<SubmitButtonWithConfirmation
									title={'base.shared.edit'}
									onSubmit={() => {}}
									confirmationTitle={'base.shared.edit'}
									confirmationText={
										'component.onlineOrdering.editMultipleScheduleConfirmationMessage'
									}
									customButtons={() => (
										<React.Fragment>
											<SubmitButton
												onSubmit={handleUpdate}
												title={'base.editOnlyThisOne'}
											/>
											<SubmitButton
												onSubmit={values => handleUpdate(values, true)}
												title={'base.editAlsoFutures'}
											/>
										</React.Fragment>
									)}
								/>
							) : (
								<SubmitButton
									onSubmit={handleUpdate}
									title={'base.shared.edit'}
								/>
							)}
							<LinkButton
								theme={Theme.RED}
								title='base.deleteSetup'
								onClick={onDelete}
								firstUpper
							/>
						</>
					)}
				</div>
			</FormWrapper>
		</>
	);
};

export default withForm(CalendarWorkplaceOrderingSettingsEvent);
